import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useI18n } from "@wato/i18n/react";
import { cx } from "../../cx";
import { Icon } from "../../rtl/glyphs";
import { IconButton } from "../IconButton/IconButton";

export type BannerSeverity = "info" | "success" | "warning" | "danger";

const TONE: Record<BannerSeverity, string> = {
  info: "bg-info-bg text-info-fg border-info-icon",
  success: "bg-success-bg text-success-fg border-success-icon",
  warning: "bg-warning-bg text-warning-fg border-warning-icon",
  danger: "bg-danger-bg text-danger-bg-fg border-danger-icon",
};
const ICON = { info: "info", success: "check", warning: "warning", danger: "warning" } as const;
const STORE = "wato.banners.dismissed";

function readDismissed(): string[] {
  try {
    const raw = window.localStorage.getItem(STORE);
    const v: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === "string") : [];
  } catch {
    return [];
  }
}
function persist(ids: string[]) {
  try {
    window.localStorage.setItem(STORE, JSON.stringify(ids));
  } catch {
    /* storage blocked: the dismissal lasts for this page view only */
  }
}

interface SlotApi {
  readonly register: (id: string, priority: number) => () => void;
  readonly isActive: (id: string) => boolean;
  readonly dismiss: (id: string) => void;
  readonly dismissed: ReadonlySet<string>;
}
const SlotContext = createContext<SlotApi | null>(null);

/** Queues its `DismissibleBanner` children: at most one is visible at a time (G10). */
export function BannerSlot({ children }: { readonly children: ReactNode }) {
  const [order, setOrder] = useState<{ id: string; priority: number }[]>([]);
  const [dismissed, setDismissed] = useState<ReadonlySet<string>>(() => new Set(readDismissed()));
  const register = useCallback((id: string, priority: number) => {
    setOrder((o) =>
      o.some((x) => x.id === id)
        ? o
        : // Stable sort: lower priority number first, then registration order.
          [...o, { id, priority }].sort((a, b) => a.priority - b.priority),
    );
    return () => setOrder((o) => o.filter((x) => x.id !== id));
  }, []);
  const dismiss = useCallback((id: string) => {
    setDismissed((d) => {
      const next = new Set(d).add(id);
      persist([...next]);
      return next;
    });
  }, []);
  const api = useMemo<SlotApi>(() => {
    const first = order.find((x) => !dismissed.has(x.id))?.id;
    return { register, dismiss, dismissed, isActive: (id) => id === first };
  }, [order, dismissed, register, dismiss]);
  return <SlotContext.Provider value={api}>{children}</SlotContext.Provider>;
}

export interface DismissibleBannerProps {
  /** Stable id: the dismissal is remembered per browser under this key. */
  readonly id: string;
  readonly severity?: BannerSeverity;
  readonly children: ReactNode;
  /** One optional action (link or button). */
  readonly action?: ReactNode;
  /** Set false for security/billing banners that must reappear. */
  readonly dismissible?: boolean;
  /** Queue rank inside a `BannerSlot`: lower shows first (default 100); ties keep mount order. */
  readonly priority?: number;
  /** Called once when the viewer closes the banner (e.g. to also record a server-side dismissal). */
  readonly onDismiss?: () => void;
  readonly testId?: string;
}

/** One-line banner with a single optional action and a close icon that persists the dismissal. */
export function DismissibleBanner({
  id,
  severity = "info",
  children,
  action,
  dismissible = true,
  priority = 100,
  onDismiss,
  testId,
}: DismissibleBannerProps) {
  const { t } = useI18n();
  const slot = useContext(SlotContext);
  const [local, setLocal] = useState(() => dismissible && readDismissed().includes(id));
  const register = slot?.register;
  useEffect(() => register?.(id, priority), [register, id, priority]);
  const hidden = slot ? slot.dismissed.has(id) || !slot.isActive(id) : local;
  if (hidden) return null;
  return (
    <div
      role={severity === "danger" || severity === "warning" ? "alert" : "status"}
      data-testid={testId ?? `banner-${id}`}
      data-banner-id={id}
      className={cx("flex min-h-9 items-center gap-2 border-b px-3 py-1.5 text-sm", TONE[severity])}
    >
      <Icon name={ICON[severity]} size={16} />
      <div className="min-w-0 flex-1 truncate">{children}</div>
      {action}
      {dismissible && (
        <IconButton
          label={t("ui.banner.dismiss")}
          icon={<Icon name="close" size={16} />}
          variant="ghost"
          onClick={() => {
            onDismiss?.();
            if (slot) slot.dismiss(id);
            else {
              persist([...new Set([...readDismissed(), id])]);
              setLocal(true);
            }
          }}
        />
      )}
    </div>
  );
}
